import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadVerbsCsv } from './utils/loadVerbsCsv';
import { conjugatePastGeneric_Salim } from './utils/arPastGeneric_salim';
import { conjugatePresentFormI_Salim_fromRow } from './utils/arPresentFormI_salim';
import { conjugatePresentFormII_Salim_fromRow } from './utils/arPresentFormII_salim';
import { conjugatePresentFormIII_Salim_fromRow } from './utils/arPresentFormIII_salim';
import { conjugatePresentFormIV_Salim_fromRow } from './utils/arPresentFormIV_salim';
import { conjugatePresentFormV_Salim_fromRow } from './utils/arPresentFormV_salim';
import { buildImperativeFromRow } from './utils/imperativeFormI_salim';
import { buildImperativeFromRowII } from './utils/imperativeFormII_salim';
import { buildImperativeFromRowIII } from './utils/imperativeFormIII_salim';
import { buildImperativeFromRowIV } from './utils/imperativeFormIV_salim';
import html2canvas from 'html2canvas';
import './App.css';

// מיפוי מפתחות ציווי לכינויי גוף ערביים
const IMPERATIVE_PRONOUN_MAP = {
  'you_ms': 'أَنْتَ',
  'you_fs': 'أَنْتِ',
  'you_mp': 'أَنْتُمْ',
  'you_fp': 'أَنْتُنَّ'
};

// הגופים האפשריים בציווי
const IMPERATIVE_PRONOUNS = ['أَنْتَ', 'أَنْتِ', 'أَنْتُمْ', 'أَنْتُنَّ'];

const PRONOUNS = [
  'أَنَا','أَنْتَ','أَنْتِ','هُوَ','هِيَ',
  'نَحْنُ','أَنْتُمْ','أَنْتُنَّ','هُمْ','هُنَّ'
];

const PRONOUNS_MOBILE = [
  'أَنَا','نَحْنُ','أَنْتَ','أَنْتُمْ','أَنْتِ',
  'أَنْتُنَّ','هُوَ','هُمْ','هِيَ','هُنَّ'
];

const PRESENT_LIMITED_PRONOUNS = ['أَنْتَ', 'هِيَ'];
const isPresentLimitedPronoun = (pronoun) => PRESENT_LIMITED_PRONOUNS.includes(pronoun);

const removeArabicNiqqud = (str) => (str || '').replace(/[\u064B-\u065F]/g, '');
const normalizeArabicForCompare = (str) =>
  removeArabicNiqqud(str)
    .replace(/\u0640/g, '') // tatweel
    .replace(/[إأآ]/g, 'ا')
    .trim();

const JUNIOR_SINGULAR_PRONOUNS = ['أَنَا', 'أَنْتَ', 'أَنْتِ', 'هُوَ', 'هِيَ'];
const JUNIOR_ALLOWED_VERBS_SET = new Set(
  ['اكل', 'أكل', 'ركب', 'قرا', 'قرأ', 'كتب', 'بدأ', 'ترك', 'ذهب', 'هرب', 'نزل', 'شرب'].map(normalizeArabicForCompare)
);

// Define game configurations
const gameConfigs = {
  juniorWhoDidItPastSingularSelectedVerbs: {
    title: 'מי עשה את זה (יחיד)',
    csvPath: '/verbs-grade10.csv',
    binyan: '1',
    tense: 'עבר',
    answerOptions: JUNIOR_SINGULAR_PRONOUNS,
    selectionMode: 'junior-two-rounds',
    filter: (r) => {
      const binyan = String(r['בניין'] || '').trim();
      const tense = String(r['זמן'] || '').trim();
      const lemma = normalizeArabicForCompare(r['lemma3ms'] || r['פועל'] || '');
      const isBinyan1 = binyan === '1' || binyan === 'בניין 1';
      const isPast = tense === 'עבר';
      const isAllowedVerb = JUNIOR_ALLOWED_VERBS_SET.has(lemma);
      return isBinyan1 && isPast && isAllowedVerb;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoDidItPastBinyan1: {
    title: 'מי עשה את זה? — עבר, בניין 1',
    csvPath: '/verbs-grade10.csv',
    binyan: '1',
    filter: (r) => {
      const binyanIs1 = r['בניין'] === '1' || r['בניין'] === 'בניין 1';
      const tensePast  = r['זמן'] === 'עבר';
      const salim      = (r['gizra'] || '').includes('שלמים');
      return binyanIs1 && tensePast && salim;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoDidItPastBinyan2: {
    title: 'מי עשה את זה? — עבר, בניין 2',
    csvPath: '/verbs-grade10.csv',
    binyan: '2',
    uniqueVerbs: true,
    filter: (r) => {
      const binyanIs2 = r['בניין'] === '2' || r['בניין'] === 'בניין 2';
      const tensePast  = r['זמן'] === 'עבר';
      const g = r['gizra'] || '';
      const gizraOk = g.includes('שלמים') || g.includes('כפולים') || g.includes('ע"ו') || g.includes('פ"ו');
      return binyanIs2 && tensePast && gizraOk;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoDidItPastBinyan3: {
    title: 'מי עשה את זה? — עבר, בניין 3',
    csvPath: '/verbs-grade10.csv',
    binyan: '3',
    uniqueVerbs: true,
    filter: (r) => {
      const binyanIs3 = r['בניין'] === '3' || r['בניין'] === 'בניין 3';
      const tensePast  = r['זמן'] === 'עבר';
      const g = r['gizra'] || '';
      const gizraOk = g.includes('שלמים') || g.includes('ע"ו') || g.includes('פ"ו');
      return binyanIs3 && tensePast && gizraOk;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoDidItPastBinyan4: {
    title: 'מי עשה את זה? — עבר, בניין 4',
    csvPath: '/verbs-grade10.csv',
    binyan: '4',
    uniqueVerbs: true,
    filter: (r) => {
      const binyanIs4 = r['בניין'] === '4' || r['בניין'] === 'בניין 4';
      const tensePast  = r['זמן'] === 'עבר';
      const salim      = (r['gizra'] || '').includes('שלמים');
      return binyanIs4 && tensePast && salim;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoDidItPastBinyan5: {
    title: 'מי עשה את זה? — עבר, בניין 5',
    csvPath: '/verbs-grade10.csv',
    binyan: '5',
    filter: (r) => {
      const binyanIs5 = r['בניין'] === '5' || r['בניין'] === 'בניין 5';
      const tensePast  = r['זמן'] === 'עבר';
      const salim      = (r['gizra'] || '').includes('שלמים');
      return binyanIs5 && tensePast && salim;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoDidItPastBinyan6: {
    title: 'מי עשה את זה? — עבר, בניין 6',
    csvPath: '/verbs-grade10.csv',
    binyan: '6',
    filter: (r) => {
      const binyanIs6 = r['בניין'] === '6' || r['בניין'] === 'בניין 6';
      const tensePast  = r['זמן'] === 'עבר';
      const salim      = (r['gizra'] || '').includes('שלמים');
      return binyanIs6 && tensePast && salim;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoDidItPastBinyan7: {
    title: 'מי עשה את זה? — עבר, בניין 7',
    csvPath: '/verbs-grade10.csv',
    binyan: '7',
    filter: (r) => {
      const binyanIs7 = r['בניין'] === '7' || r['בניין'] === 'בניין 7';
      const tensePast  = r['זמן'] === 'עבר';
      const salim      = (r['gizra'] || '').includes('שלמים');
      return binyanIs7 && tensePast && salim;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoDidItPastBinyan8: {
    title: 'מי עשה את זה? — עבר, בניין 8',
    csvPath: '/verbs-grade10.csv',
    binyan: '8',
    filter: (r) => {
      const binyanIs8 = r['בניין'] === '8' || r['בניין'] === 'בניין 8';
      const tensePast  = r['זמן'] === 'עבר';
      const salim      = (r['gizra'] || '').includes('שלמים');
      return binyanIs8 && tensePast && salim;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoDidItPastBinyan10: {
    title: 'מי עשה את זה? — עבר, בניין 10',
    csvPath: '/verbs-grade10.csv',
    binyan: '10',
    filter: (r) => {
      const binyanIs10 = r['בניין'] === '10' || r['בניין'] === 'בניין 10';
      const tensePast  = r['זמן'] === 'עבר';
      const salim      = (r['gizra'] || '').includes('שלמים');
      return binyanIs10 && tensePast && salim;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoDidItFutureBinyan1: {
    title: 'מי עשה את זה? — עתיד, בניין 1',
    csvPath: '/verbs-grade10.csv',
    binyan: '1',
    filter: (r) => {
      const binyanIs1 = r['בניין'] === '1' || r['בניין'] === 'בניין 1';
      const tenseFuture = r['זמן'] === 'עתיד';
      const salim      = (r['gizra'] || '').includes('שלמים');
      return binyanIs1 && tenseFuture && salim;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoDidItFutureBinyan2: {
    title: 'מי עשה את זה? — עתיד, בניין 2',
    csvPath: '/verbs-grade10.csv',
    binyan: '2',
    filter: (r) => {
      const binyanIs2 = r['בניין'] === '2' || r['בניין'] === 'בניין 2';
      const tenseFuture = r['זמן'] === 'עתיד';
      const g = r['gizra'] || '';
      const gizraOk = g.includes('שלמים') || g.includes('כפולים') || g.includes('ע"ו') || g.includes('פ"ו');
      return binyanIs2 && tenseFuture && gizraOk;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoWillDoItPresentBinyan2: {
    title: 'מי עשה את זה? — הווה-עתיד, בניין 2',
    csvPath: '/verbs-grade10.csv',
    binyan: '2',
    tense: 'עתיד',
    uniqueVerbs: true,
    filter: (r) => {
      const binyanIs2 = r['בניין'] === '2' || r['בניין'] === 'בניין 2';
      const tensePast = r['זמן'] === 'עבר';
      const g = r['gizra'] || '';
      const gizraOk = g.includes('שלמים') || g.includes('כפולים') || g.includes('ע"ו') || g.includes('פ"ו');
      return binyanIs2 && tensePast && gizraOk;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoWillDoItPresentBinyan3: {
    title: 'מי עשה את זה? — הווה-עתיד, בניין 3',
    csvPath: '/verbs-grade10.csv',
    binyan: '3',
    tense: 'עתיד',
    uniqueVerbs: true,
    filter: (r) => {
      const binyanIs3 = r['בניין'] === '3' || r['בניין'] === 'בניין 3';
      const tensePast = r['זמן'] === 'עבר';
      const g = r['gizra'] || '';
      const gizraOk = g.includes('שלמים') || g.includes('ע"ו') || g.includes('פ"ו');
      return binyanIs3 && tensePast && gizraOk;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoWillDoItPresentBinyan4: {
    title: 'מי עשה את זה? — הווה-עתיד, בניין 4',
    csvPath: '/verbs-grade10.csv',
    binyan: '4',
    tense: 'עתיד',
    filter: (r) => {
      const binyanIs4 = r['בניין'] === '4' || r['בניין'] === 'בניין 4';
      const tensePast = r['זמן'] === 'עבר';
      const salim = (r['gizra'] || '').includes('שלמים');
      return binyanIs4 && tensePast && salim;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoWillDoItPresentBinyan5: {
    title: 'מי עשה את זה? — הווה-עתיד, בניין 5',
    csvPath: '/verbs-grade10.csv',
    binyan: '5',
    tense: 'עתיד',
    uniqueVerbs: true,
    filter: (row) => {
      const binyan = String(row['בניין'] || '').trim();
      const gizra = row.gizra || '';
      return (binyan === '5' || binyan === 'בניין 5') &&
        row['זמן'] === 'עבר' &&
        ['שלמים', 'כפולים', 'ע"ע', 'פ"ו', 'פ"י', 'ע"ו', 'ע"י'].some(pattern => gizra.includes(pattern));
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoDidItAllBinyansPast: {
    title: 'מי עשה את זה? — עבר, כל הבניינים',
    csvPath: '/verbs-grade10.csv',
    binyan: 'all',
    tense: 'עבר',
    filter: (r) => {
      const binyan = r['בניין'] || '';
      const validBinyan = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10',
                           'בניין 1', 'בניין 2', 'בניין 3', 'בניין 4', 'בניין 5',
                           'בניין 6', 'בניין 7', 'בניין 8', 'בניין 9', 'בניין 10'].includes(binyan);
      const tensePast = r['זמן'] === 'עבר';
      const salim = (r['gizra'] || '').includes('שלמים');
      return validBinyan && tensePast && salim;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoWillDoItPresentBinyan1: {
    title: 'מי עשה את זה? — הווה-עתיד, בניין 1',
    csvPath: '/verbs-grade10.csv',
    binyan: '1',
    tense: 'עתיד',
    filter: (r) => {
      const binyanIs1 = r['בניין'] === '1' || r['בניין'] === 'בניין 1';
      const tensePast = r['זמן'] === 'עבר';
      const salim = (r['gizra'] || '').includes('שלמים');
      return binyanIs1 && tensePast && salim;
    },
    questionType: 'pronoun',
    resultTitle: 'מי עשה את זה',
  },
  whoDidItBothTensesBinyan1: {
    title: 'בניין 1 שני זמנים — עבר + הווה-עתיד',
    csvPath: '/verbs-grade10.csv',
    binyan: '1',
    tense: 'שניהם',
    filter: (r) => {
      const binyanIs1 = r['בניין'] === '1' || r['בניין'] === 'בניין 1';
      const tensePast = r['זמן'] === 'עבר';
      const salim = (r['gizra'] || '').includes('שלמים');
      return binyanIs1 && tensePast && salim;
    },
    questionType: 'pronoun',
    resultTitle: 'בניין 1 שני זמנים',
  },
  whoDidItBothTensesBinyan2: {
    title: 'בניין 2 שני זמנים — עבר + הווה-עתיד',
    csvPath: '/verbs-grade10.csv',
    binyan: '2',
    tense: 'שניהם',
    uniqueVerbs: true,
    filter: (r) => {
      const binyanIs2 = r['בניין'] === '2' || r['בניין'] === 'בניין 2';
      const tensePast = r['זמן'] === 'עבר';
      const g = r['gizra'] || '';
      const gizraOk = g.includes('שלמים') || g.includes('כפולים') || g.includes('ע"ו') || g.includes('פ"ו');
      return binyanIs2 && tensePast && gizraOk;
    },
    questionType: 'pronoun',
    resultTitle: 'בניין 2 שני זמנים',
  },
  whoDidItBothTensesBinyan3: {
    title: 'בניין 3 שני זמנים — עבר + הווה-עתיד',
    csvPath: '/verbs-grade10.csv',
    binyan: '3',
    tense: 'שניהם',
    uniqueVerbs: true,
    filter: (r) => {
      const binyanIs3 = r['בניין'] === '3' || r['בניין'] === 'בניין 3';
      const tensePast = r['זמן'] === 'עבר';
      const g = r['gizra'] || '';
      const gizraOk = g.includes('שלמים') || g.includes('ע"ו') || g.includes('פ"ו');
      return binyanIs3 && tensePast && gizraOk;
    },
    questionType: 'pronoun',
    resultTitle: 'בניין 3 שני זמנים',
  },
  whoDidItBothTensesBinyan4: {
    title: 'בניין 4 שני זמנים — עבר + הווה-עתיד',
    csvPath: '/verbs-grade10.csv',
    binyan: '4',
    tense: 'שניהם',
    filter: (r) => {
      const binyanIs4 = r['בניין'] === '4' || r['בניין'] === 'בניין 4';
      const tensePast = r['זמן'] === 'עבר';
      const salim = (r['gizra'] || '').includes('שלמים');
      return binyanIs4 && tensePast && salim;
    },
    questionType: 'pronoun',
    resultTitle: 'בניין 4 שני זמנים',
  },
  whoDidItAllThreeTensesBinyan1: {
    title: 'בניין 1 שלושה זמנים — עבר + הווה-עתיד + ציווי',
    csvPath: '/verbs-grade10.csv',
    binyan: '1',
    tense: 'שלושתם',
    filter: (r) => {
      const binyanIs1 = r['בניין'] === '1' || r['בניין'] === 'בניין 1';
      const tensePast = r['זמן'] === 'עבר';
      const salim = (r['gizra'] || '').includes('שלמים');
      const hasFaVowelFuture = r['R2_FUTURE'] || r['fa_vowel_future'];
      return binyanIs1 && tensePast && salim && hasFaVowelFuture;
    },
    questionType: 'pronoun',
    resultTitle: 'בניין 1 שלושה זמנים',
  },
  whoDidItAllThreeTensesBinyan2: {
    title: 'בניין 2 שלושה זמנים — עבר + הווה-עתיד + ציווי',
    csvPath: '/verbs-grade10.csv',
    binyan: '2',
    tense: 'שלושתם',
    uniqueVerbs: true,
    filter: (r) => {
      const binyanIs2 = r['בניין'] === '2' || r['בניין'] === 'בניין 2';
      const tensePast = r['זמן'] === 'עבר';
      const g = r['gizra'] || '';
      const gizraOk = g.includes('שלמים') || g.includes('כפולים') || g.includes('ע"ו') || g.includes('פ"ו');
      return binyanIs2 && tensePast && gizraOk;
    },
    questionType: 'pronoun',
    resultTitle: 'בניין 2 שלושה זמנים',
  },
  whoDidItAllThreeTensesBinyan3: {
    title: 'בניין 3 שלושה זמנים — עבר + הווה-עתיד + ציווי',
    csvPath: '/verbs-grade10.csv',
    binyan: '3',
    tense: 'שלושתם',
    uniqueVerbs: true,
    filter: (r) => {
      const binyanIs3 = r['בניין'] === '3' || r['בניין'] === 'בניין 3';
      const tensePast = r['זמן'] === 'עבר';
      const g = r['gizra'] || '';
      const gizraOk = g.includes('שלמים') || g.includes('ע"ו') || g.includes('פ"ו');
      return binyanIs3 && tensePast && gizraOk;
    },
    questionType: 'pronoun',
    resultTitle: 'בניין 3 שלושה זמנים',
  },
  whoDidItAllThreeTensesBinyan4: {
    title: 'בניין 4 שלושה זמנים — עבר + הווה-עתיד + ציווי',
    csvPath: '/verbs-grade10.csv',
    binyan: '4',
    tense: 'שלושתם',
    filter: (r) => {
      const binyanIs4 = r['בניין'] === '4' || r['בניין'] === 'בניין 4';
      const tensePast = r['זמן'] === 'עבר';
      const salim = (r['gizra'] || '').includes('שלמים');
      return binyanIs4 && tensePast && salim;
    },
    questionType: 'pronoun',
    resultTitle: 'בניין 4 שלושה זמנים',
  },
};


export default function VerbGameWhoDidIt({ csvPath, gameType, backUrlOverride }) {
  const navigate = useNavigate();
  const { grade } = useParams();
  const location = useLocation();
  const fromBinyan = location.state?.fromBinyan;
  
  const getBackUrl = () => {
    if (backUrlOverride) return backUrlOverride;
    if (fromBinyan) {
      return `/grade/${grade}/verbs/binyan/${fromBinyan}`;
    }
    return `/grade/${grade}/verbs`;
  };

  // Map gameType from URL to config key
  const gameConfigMap = {
    'binyan1-past': 'whoDidItPastBinyan1',
    'binyan2-past': 'whoDidItPastBinyan2',
    'binyan3-past': 'whoDidItPastBinyan3',
    'binyan4-past': 'whoDidItPastBinyan4',
    'binyan5-past': 'whoDidItPastBinyan5',
    'binyan6-past': 'whoDidItPastBinyan6',
    'binyan7-past': 'whoDidItPastBinyan7',
    'binyan8-past': 'whoDidItPastBinyan8',
    'binyan10-past': 'whoDidItPastBinyan10',
    'all-binyans-past': 'whoDidItAllBinyansPast',
    'binyan1-present': 'whoWillDoItPresentBinyan1',
    'binyan1-both-tenses': 'whoDidItBothTensesBinyan1',
    'binyan1-all-three-tenses': 'whoDidItAllThreeTensesBinyan1',
    'binyan1-future': 'whoDidItFutureBinyan1',
    'binyan2-future': 'whoDidItFutureBinyan2',
    'binyan2-present': 'whoWillDoItPresentBinyan2',
    'binyan2-both-tenses': 'whoDidItBothTensesBinyan2',
    'binyan3-both-tenses': 'whoDidItBothTensesBinyan3',
    'binyan3-present': 'whoWillDoItPresentBinyan3',
    'binyan4-present': 'whoWillDoItPresentBinyan4',
    'binyan5-present': 'whoWillDoItPresentBinyan5',
    'binyan4-both-tenses': 'whoDidItBothTensesBinyan4',
    'binyan2-all-three-tenses': 'whoDidItAllThreeTensesBinyan2',
    'binyan3-all-three-tenses': 'whoDidItAllThreeTensesBinyan3',
    'binyan4-all-three-tenses': 'whoDidItAllThreeTensesBinyan4',
    'junior-who-did-it-singular': 'juniorWhoDidItPastSingularSelectedVerbs'
  };

  const configKey = gameConfigMap[gameType] || 'whoDidItPastBinyan2';
  const gameConfig = gameConfigs[configKey];

  const [items, setItems] = useState([]); // [{base,row,conj,questionForm,answerPronoun}]
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [clickedButton, setClickedButton] = useState(null);
  const [showLine, setShowLine] = useState(false);
  const [lineCoords, setLineCoords] = useState({ x1: 0, y1: 0, x2: 0, y2: 0 });
  const [showFireworks, setShowFireworks] = useState(false);
  const [userAnswers, setUserAnswers] = useState({}); // Track user's answers
  const [showResultsPopup, setShowResultsPopup] = useState(false);
  const resultsSummaryRef = useRef(null);
  
  // Create a single AudioContext for all sounds (mobile optimization)
  const audioContextRef = useRef(null);
  
  useEffect(() => {
    // חמם את ה-AudioContext מיד בכניסה למשחק (הכניסה עצמה היא לחיצת משתמש)
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current.resume();
    } catch (e) {}
    // Clean up on unmount
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  const handleBackToSubjects = () => {
    navigate(getBackUrl());
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const all = await loadVerbsCsv(gameConfig.csvPath);

        // מסננים: לפי הגדרות המשחק
        const filtered = all.filter(gameConfig.filter);

        // מייצרים את כל האפשרויות
        const allOptions = [];
        
        // עבור משחק "שני זמנים" - נייצר אפשרויות גם מעבר וגם מהווה-עתיד
        // עבור משחק "שלושה זמנים" - נייצר גם עבר, גם הווה-עתיד וגם ציווי
        let tensesToGenerate;
        if (gameConfig.tense === 'שלושתם') {
          tensesToGenerate = ['עבר', 'עתיד', 'ציווי'];
        } else if (gameConfig.tense === 'שניהם') {
          tensesToGenerate = ['עבר', 'עתיד'];
        } else {
          tensesToGenerate = [gameConfig.tense || 'עבר'];
        }
        const hasPresentTense = tensesToGenerate.includes('עתיד');
        const isPresentOnlyGame = tensesToGenerate.length === 1 && tensesToGenerate[0] === 'עתיד';
        
        for (const row of filtered) {
          // בודקים אם יש הגבלה על גופים מסוימים
          let allowedPronouns = PRONOUNS;
          if (row['allowed_pronouns']) {
            // פונקציה להסרת ניקוד לצורך השוואה
            const removeNiqqud = (str) => str.replace(/[\u064B-\u065F]/g, '');
            
            // מפצלים לפי נקודה-פסיק (;) ומנקים רווחים
            const allowedList = row['allowed_pronouns']
              .split(';')
              .map(p => p.trim())
              .filter(p => p);
            
            // מוצאים את הגופים המתאימים מ-PRONOUNS (בהתאם לשמות ללא ניקוד)
            allowedPronouns = [];
            for (const allowed of allowedList) {
              const allowedNoNiqqud = removeNiqqud(allowed);
              for (const pron of PRONOUNS) {
                if (removeNiqqud(pron) === allowedNoNiqqud) {
                  allowedPronouns.push(pron);
                  break;
                }
              }
            }
          }
          
          // יוצרים אפשרויות עבור כל זמן
          for (const tenseType of tensesToGenerate) {
            // בוחרים את פונקציית הנטייה לפי הזמן
            let conj;
            let pronounsForTense = allowedPronouns;
            const answerOptionsForGame = gameConfig.answerOptions || null;
            
            if (tenseType === 'עתיד') {
              conj = conjugatePresentFormV_Salim_fromRow(row) || conjugatePresentFormIV_Salim_fromRow(row) || conjugatePresentFormIII_Salim_fromRow(row) || conjugatePresentFormII_Salim_fromRow(row) || conjugatePresentFormI_Salim_fromRow(row);
            } else if (tenseType === 'ציווי') {
              // בציווי יש רק 4 גופים
              const imperativeConj = gameConfig.binyan === '4'
                ? buildImperativeFromRowIV(row)
                : gameConfig.binyan === '3'
                  ? buildImperativeFromRowIII(row)
                  : gameConfig.binyan === '2'
                    ? buildImperativeFromRowII(row)
                    : buildImperativeFromRow(row);
              if (!imperativeConj) continue;
              
              // ממירים את צורות הציווי לפורמט של כינויי גוף ערביים
              conj = {};
              for (const [key, value] of Object.entries(imperativeConj)) {
                const arabicPronoun = IMPERATIVE_PRONOUN_MAP[key];
                if (arabicPronoun) {
                  conj[arabicPronoun] = value;
                }
              }
              // בציווי רק 4 גופים אפשריים
              pronounsForTense = IMPERATIVE_PRONOUNS.filter(p => allowedPronouns.includes(p) || allowedPronouns === PRONOUNS);
            } else {
              conj = conjugatePastGeneric_Salim(row['פועל']);
            }
            if (!conj) continue;

            if (answerOptionsForGame && tenseType !== 'ציווי') {
              pronounsForTense = pronounsForTense.filter((p) => answerOptionsForGame.includes(p));
            }
            
            // יוצרים אפשרות רק לגופים המותרים
            for (const pron of pronounsForTense) {
              // וידוא שיש צורה לגוף הזה
              if (!conj[pron]) continue;
              
              allOptions.push({
                base: row['פועל'],
                row,
                conj,
                tense: tenseType,
                answerPronoun: pron,
                questionForm: conj[pron],
                options: answerOptionsForGame
                  ? answerOptionsForGame
                  : PRONOUNS
              });
            }
          }
        }

        // בוחרים 10 פריטים עם הגרלה:
        // - כל גוף שעדיין לא הופיע -> מאושר
        // - הגוף הראשון שמופיע פעם שנייה -> מאושר (זה יהיה הגוף היחיד שחוזר)
        // - כל גוף נוסף שכבר הופיע -> נדחה
        // תוצאה: 10 גופים שונים או 9 גופים + 1 שחוזר
        
        // במשחק "שני זמנים" - נבחר בדיוק 5 מעבר ו-5 מעתיד
        // במשחק "שלושה זמנים" - נבחר 3 מכל זמן ועוד שאלה אחת אקראית
        let selected = [];

        const createPresentPronounPolicy = () => ({
          usedPronouns: new Set(),
          hasFutureLimitedPronoun: false
        });

        const canUsePresentPronounPolicy = (item, policy) => {
          if (!policy || !isPresentLimitedPronoun(item.answerPronoun)) return true;
          if (policy.usedPronouns.has(item.answerPronoun)) return false;
          if (item.tense === 'עתיד' && policy.hasFutureLimitedPronoun) return false;
          return true;
        };

        const recordPresentPronounPolicy = (item, policy) => {
          if (!policy || !isPresentLimitedPronoun(item.answerPronoun)) return;
          policy.usedPronouns.add(item.answerPronoun);
          if (item.tense === 'עתיד') {
            policy.hasFutureLimitedPronoun = true;
          }
        };
        
        // פונקציה לבחירת N פריטים מרשימה
        // uniquePronouns = true אומר שכל גוף יכול להופיע רק פעם אחת
        const selectNItems = (optionsList, count, uniquePronouns = false, presentPronounPolicy = null, initialItems = []) => {
          const selectedItems = [];
          const pronounCount = {};
          const baseCount = {};
          const basePronounPairs = new Set();
          let hasRepeatingPronoun = false;

          const rememberItem = (item) => {
            const pairKey = `${item.base}|${item.answerPronoun}|${item.tense}`;
            const currentPronounCount = pronounCount[item.answerPronoun] || 0;

            if (currentPronounCount > 0) {
              hasRepeatingPronoun = true;
            }

            pronounCount[item.answerPronoun] = currentPronounCount + 1;
            baseCount[item.base] = (baseCount[item.base] || 0) + 1;
            basePronounPairs.add(pairKey);
          };

          initialItems.forEach((item) => {
            rememberItem(item);
            recordPresentPronounPolicy(item, presentPronounPolicy);
          });
          
          const shuffledList = shuffle([...optionsList]);
          
          for (const item of shuffledList) {
            if (selectedItems.length >= count) break;
            
            const pairKey = `${item.base}|${item.answerPronoun}|${item.tense}`;
            const currentPronounCount = pronounCount[item.answerPronoun] || 0;
            const currentBaseCount = baseCount[item.base] || 0;
            
            if (basePronounPairs.has(pairKey)) continue;
            const maxPerBase = gameConfig.uniqueVerbs ? 1 : 2;
            if (currentBaseCount >= maxPerBase) continue;
            if (!canUsePresentPronounPolicy(item, presentPronounPolicy)) continue;
            
            if (currentPronounCount === 0) {
              // גוף חדש -> תמיד מאושר
            } else if (!uniquePronouns && currentPronounCount === 1 && !hasRepeatingPronoun) {
              // מאפשרים חזרה אחת רק אם לא דרשנו גופים ייחודיים
              hasRepeatingPronoun = true;
            } else {
              continue;
            }
            
            selectedItems.push(item);
            rememberItem(item);
            recordPresentPronounPolicy(item, presentPronounPolicy);
          }
          
          return selectedItems;
        };

        const selectPresentOnlyItems = () => {
          const selectedItems = [];
          const pronounCount = {};
          const baseCount = {};
          const basePronounPairs = new Set();
          let hasRepeatingPronoun = false;
          let limitedPronounCount = 0;

          for (const item of shuffle([...allOptions])) {
            if (selectedItems.length >= 10) break;

            const pairKey = `${item.base}|${item.answerPronoun}|${item.tense}`;
            const currentPronounCount = pronounCount[item.answerPronoun] || 0;
            const currentBaseCount = baseCount[item.base] || 0;
            const isLimitedPresentPronoun = isPresentLimitedPronoun(item.answerPronoun);

            if (basePronounPairs.has(pairKey)) continue;
            const maxPerBase = gameConfig.uniqueVerbs ? 1 : 2;
            if (currentBaseCount >= maxPerBase) continue;
            if (isLimitedPresentPronoun && limitedPronounCount >= 2) continue;

            if (currentPronounCount === 0) {
              // גוף חדש -> תמיד מאושר
            } else if (currentPronounCount === 1 && !hasRepeatingPronoun) {
              hasRepeatingPronoun = true;
            } else {
              continue;
            }

            selectedItems.push(item);
            pronounCount[item.answerPronoun] = currentPronounCount + 1;
            baseCount[item.base] = currentBaseCount + 1;
            basePronounPairs.add(pairKey);
            if (isLimitedPresentPronoun) limitedPronounCount += 1;
          }

          return selectedItems;
        };

        const hasAdjacentRepeatedPronouns = (itemsList) =>
          itemsList.some((item, index) => index > 0 && item.answerPronoun === itemsList[index - 1].answerPronoun);

        const arrangeWithoutAdjacentRepeatedPronouns = (itemsList) => {
          if (!hasAdjacentRepeatedPronouns(itemsList)) return itemsList;

          for (let attempt = 0; attempt < 40; attempt++) {
            const candidate = shuffle(itemsList);
            if (!hasAdjacentRepeatedPronouns(candidate)) return candidate;
          }

          const arranged = [...itemsList];
          for (let index = 1; index < arranged.length; index++) {
            if (arranged[index].answerPronoun !== arranged[index - 1].answerPronoun) continue;

            const [itemToMove] = arranged.splice(index, 1);
            let inserted = false;

            for (let insertAt = 0; insertAt <= arranged.length; insertAt++) {
              const previous = arranged[insertAt - 1];
              const next = arranged[insertAt];
              if (
                previous?.answerPronoun !== itemToMove.answerPronoun &&
                next?.answerPronoun !== itemToMove.answerPronoun
              ) {
                arranged.splice(insertAt, 0, itemToMove);
                inserted = true;
                break;
              }
            }

            if (!inserted) arranged.splice(index, 0, itemToMove);
          }

          return arranged;
        };
        
        if (gameConfig.selectionMode === 'junior-two-rounds') {
          const requiredPronouns = gameConfig.answerOptions || JUNIOR_SINGULAR_PRONOUNS;
          const requiredCount = requiredPronouns.length * 2;

          const byBase = new Map(); // baseKey -> Map(pronoun -> item)
          for (const item of allOptions) {
            const baseKey = normalizeArabicForCompare(item.base);
            if (!byBase.has(baseKey)) byBase.set(baseKey, new Map());
            byBase.get(baseKey).set(item.answerPronoun, item);
          }

          const eligibleBases = [...byBase.entries()]
            .filter(([, pronMap]) => requiredPronouns.every((p) => pronMap.has(p)))
            .map(([baseKey]) => baseKey);

          if (eligibleBases.length >= requiredCount) {
            const round1 = shuffle([...requiredPronouns]);
            let round2 = shuffle([...requiredPronouns]);

            for (let tries = 0; tries < 25 && round2.join('|') === round1.join('|'); tries++) {
              round2 = shuffle([...requiredPronouns]);
            }

            if (round2.join('|') === round1.join('|') && round2.length > 1) {
              round2 = [...round2.slice(1), round2[0]];
            }

            if (round1[round1.length - 1] === round2[0] && round2.length > 1) {
              round2 = [...round2.slice(1), round2[0]];
            }

            const pronounSequence = [...round1, ...round2];
            const chosenBases = shuffle(eligibleBases).slice(0, requiredCount);

            selected = pronounSequence.map((pron, idx) => byBase.get(chosenBases[idx]).get(pron));
          } else {
            console.warn(
              'Not enough eligible verbs for junior-two-rounds; falling back to default selection.',
              { eligibleBases: eligibleBases.length, requiredCount }
            );
          }
        } else if (gameConfig.tense === 'שלושתם') {
          // מפרידים את האפשרויות לפי זמן
          const pastOptions = allOptions.filter(item => item.tense === 'עבר');
          const futureOptions = allOptions.filter(item => item.tense === 'עתיד');
          const imperativeOptions = allOptions.filter(item => item.tense === 'ציווי');

          const createAllThreeState = () => ({
            pronounCounts: {},
            usedPronounsByTense: {
              'עבר': new Set(),
              'עתיד': new Set(),
              'ציווי': new Set()
            }
          });

          const getRepeatedPronounCount = (state) =>
            Object.values(state.pronounCounts).filter(count => count > 1).length;

          const canUseAllThreeItem = (item, state, presentPronounPolicy) => {
            if (state.usedPronounsByTense[item.tense]?.has(item.answerPronoun)) return false;
            if (!canUsePresentPronounPolicy(item, presentPronounPolicy)) return false;

            const currentPronounCount = state.pronounCounts[item.answerPronoun] || 0;
            if (currentPronounCount >= 2) return false;
            if (currentPronounCount === 1 && getRepeatedPronounCount(state) >= 2) return false;

            return true;
          };

          const rememberAllThreeItem = (item, state, presentPronounPolicy) => {
            state.usedPronounsByTense[item.tense]?.add(item.answerPronoun);
            state.pronounCounts[item.answerPronoun] = (state.pronounCounts[item.answerPronoun] || 0) + 1;
            recordPresentPronounPolicy(item, presentPronounPolicy);
          };

          const selectAllThreeItems = (optionsList, count, state, presentPronounPolicy) => {
            const selectedItems = [];
            const baseCount = {};

            for (const item of shuffle([...optionsList])) {
              if (selectedItems.length >= count) break;
              const currentBaseCount = baseCount[item.base] || 0;
              const maxPerBase = gameConfig.uniqueVerbs ? 1 : 2;
              if (currentBaseCount >= maxPerBase) continue;
              if (!canUseAllThreeItem(item, state, presentPronounPolicy)) continue;

              selectedItems.push(item);
              baseCount[item.base] = currentBaseCount + 1;
              rememberAllThreeItem(item, state, presentPronounPolicy);
            }

            return selectedItems;
          };

          for (let attempt = 0; attempt < 60 && selected.length === 0; attempt++) {
            const presentPronounPolicy = hasPresentTense ? createPresentPronounPolicy() : null;
            const allThreeState = createAllThreeState();

            // הציווי נבחר ראשון כי יש בו רק ארבעה גופים אפשריים.
            const imperativeSelected = selectAllThreeItems(imperativeOptions, 3, allThreeState, presentPronounPolicy);
            if (imperativeSelected.length < 3) continue;

            const pastSelected = selectAllThreeItems(pastOptions, 3, allThreeState, presentPronounPolicy);
            if (pastSelected.length < 3) continue;

            const futureSelected = selectAllThreeItems(futureOptions, 3, allThreeState, presentPronounPolicy);
            if (futureSelected.length < 3) continue;

            const randomExtra = selectAllThreeItems(
              [...pastOptions, ...futureOptions, ...imperativeOptions],
              1,
              allThreeState,
              presentPronounPolicy
            );
            if (randomExtra.length < 1) continue;

            selected = shuffle([...imperativeSelected, ...pastSelected, ...futureSelected, ...randomExtra]);
          }
        } else if (gameConfig.tense === 'שניהם') {
          const presentPronounPolicy = hasPresentTense ? createPresentPronounPolicy() : null;

          const pastOptions = allOptions.filter(item => item.tense === 'עבר');
          const futureOptions = allOptions.filter(item => item.tense === 'עתיד');
          
          const pastSelected = selectNItems(pastOptions, 5, true, presentPronounPolicy);
          const usedPronouns = new Set(pastSelected.map(item => item.answerPronoun));
          const usedBases = new Set(pastSelected.map(item => item.base));
          
          const futureFiltered = futureOptions.filter(item =>
            !usedPronouns.has(item.answerPronoun) &&
            !(gameConfig.uniqueVerbs && usedBases.has(item.base))
          );
          const futureSelected4 = selectNItems(futureFiltered, 4, true, presentPronounPolicy);
          
          const usedFuturePronouns = new Set(futureSelected4.map(item => item.answerPronoun));
          const usedFutureBases = new Set(futureSelected4.map(item => item.base));
          const futureRemaining = futureOptions.filter(item =>
            !usedFuturePronouns.has(item.answerPronoun) &&
            !(gameConfig.uniqueVerbs && usedFutureBases.has(item.base)) &&
            !(gameConfig.uniqueVerbs && usedBases.has(item.base))
          );
          const futureExtra = selectNItems(futureRemaining, 1, false, presentPronounPolicy);
          
          selected = shuffle([...pastSelected, ...futureSelected4, ...futureExtra]);
        } else if (isPresentOnlyGame) {
          selected = arrangeWithoutAdjacentRepeatedPronouns(selectPresentOnlyItems());
        } else {
          // משחק רגיל (לא "שני זמנים")
          const pronounCount = {};
          const baseCount = {};
          const basePronounPairs = new Set();
          let hasRepeatingPronoun = false;
          
          const shuffledAll = shuffle([...allOptions]);
          
          for (const item of shuffledAll) {
            if (selected.length >= 10) break;
            
            const pairKey = item.tense 
              ? `${item.base}|${item.answerPronoun}|${item.tense}`
              : `${item.base}|${item.answerPronoun}`;
            const currentPronounCount = pronounCount[item.answerPronoun] || 0;
            const currentBaseCount = baseCount[item.base] || 0;
            
            if (basePronounPairs.has(pairKey)) continue;
            const maxBase = gameConfig.uniqueVerbs ? 1 : 2;
            if (currentBaseCount >= maxBase) continue;
            
            if (currentPronounCount === 0) {
              // גוף חדש -> תמיד מאושר
            } else if (currentPronounCount === 1 && !hasRepeatingPronoun) {
              hasRepeatingPronoun = true;
            } else {
              continue;
            }
            
            selected.push(item);
            pronounCount[item.answerPronoun] = currentPronounCount + 1;
            baseCount[item.base] = currentBaseCount + 1;
            basePronounPairs.add(pairKey);
          }

          if (tensesToGenerate.length === 1 && tensesToGenerate[0] === 'עבר') {
            selected = arrangeWithoutAdjacentRepeatedPronouns(selected);
          }
        }

        setItems(selected);
        setIdx(0);
        setScore(0);
        setDone(false);
        setFeedback(null);
      } catch (error) {
        console.error('שגיאה בטעינת הפעלים:', error);
      }
    })();
  }, [gameConfig.csvPath, gameConfig.filter, gameConfig.tense, gameConfig.answerOptions, gameConfig.selectionMode]); // Depend on gameConfig

  const current = items[idx];
  const progress = `${Math.min(idx + 1, items.length)}/${items.length || 0}`;

  // מחזיר Promise שמסתיים רק כשה-AudioContext פועל לחלוטין
  const ensureAudioReady = async () => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return null;

      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new AudioContextClass();
      }

      if (audioContextRef.current.state !== 'running') {
        await audioContextRef.current.resume();
      }

      return audioContextRef.current.state === 'running' ? audioContextRef.current : null;
    } catch (error) {
      console.warn('AudioContext could not be initialized:', error);
      return null;
    }
  };

  // מנגן תו בודד — startTime מאפשר תזמון מדויק דרך Web Audio API
  const playNote = (frequency, duration, type = 'success', startTime = null) => {
    if (!audioContextRef.current) return;
    const audioContext = audioContextRef.current;
    const t = startTime ?? audioContext.currentTime;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    if (type === 'success') {
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, t);
      gainNode.gain.exponentialRampToValueAtTime(0.01, t + duration);
    } else {
      oscillator.frequency.value = frequency;
      oscillator.type = 'sawtooth';
      gainNode.gain.setValueAtTime(0.2, t);
      gainNode.gain.exponentialRampToValueAtTime(0.01, t + duration);
    }
    oscillator.start(t);
    oscillator.stop(t + duration);
  };

  // Share functions
  const generateResultsImage = async () => {
    if (!resultsSummaryRef.current) return null;
    const canvas = await html2canvas(resultsSummaryRef.current, {
      backgroundColor: '#5eb9c5',
      scale: 2,
    });
    return canvas.toDataURL('image/png');
  };

  const shareViaWhatsApp = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;

    const blob = dataURLtoBlob(imageData);
    const fileName = `תוצאות-${gameConfig.title.replace(/[^א-ת0-9]/g, '-')}.png`;
    const file = new File([blob], fileName, { type: 'image/png' });

    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: `תוצאות ${gameConfig.title}`,
          text: `${gameConfig.title}\nהשגתי ${score}/${items.length}!`,
          files: [file],
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      const link = document.createElement('a');
      link.download = fileName;
      link.href = imageData;
      link.click();
    }
    setShowResultsPopup(false);
  };

  const shareViaEmail = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;
    const fileName = `תוצאות-${gameConfig.title.replace(/[^א-ת0-9]/g, '-')}.png`;
    const link = document.createElement('a');
    link.download = fileName;
    link.href = imageData;
    link.click();
    setShowResultsPopup(false);
  };

  const dataURLtoBlob = (dataURL) => {
    const parts = dataURL.split(',');
    const mime = parts[0].match(/:(.*?);/)[1];
    const bstr = atob(parts[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let n = 0; n < bstr.length; n++) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  function handleChoose(option, event) {
    if (!current) return;
    
    // בדוק אם הכינוי שנבחר מייצר את אותה הצורה כמו השאלה
    // זה מאפשר תשובות מרובות נכונות (למשל, تَحْضُرُ יכול להיות أنتَ או هي)
    const correct = current.conj[option] === current.questionForm;
    
    // מצא את כל התשובות הנכונות האפשריות
    const answerPool = current.options || PRONOUNS;
    const allCorrectAnswers = answerPool.filter(pron => current.conj[pron] === current.questionForm);
    
    setClickedButton(option);
    
    // Track user's answer
    setUserAnswers(prev => ({
      ...prev,
      [idx]: { 
        userChoice: option, 
        correct: correct,
        questionForm: current.questionForm,
        correctAnswer: current.answerPronoun,
        allCorrectAnswers: allCorrectAnswers
      }
    }));
    
    if (correct) {
      setFeedback('correct');
      setScore(s => s + 1);
      
      // Play success sound sequence — תזמון מדויק דרך Web Audio API
      ensureAudioReady().then((audioContext) => {
        if (!audioContext) return;
        const t = audioContext.currentTime;
        playNote(523.25, 0.15, 'success', t);        // C5
        playNote(659.25, 0.15, 'success', t + 0.1);  // E5 — 100ms אחרי
        playNote(783.99, 0.2,  'success', t + 0.2);  // G5 — 200ms אחרי
      });
      
      // Get coordinates for the line (relative to game-container)
      // הקו יופיע רק לכינוי שהמשתמש בחר (אפילו אם יש כמה תשובות נכונות)
      const ellipse = document.querySelector('.verb-word-display');
      const button = event.currentTarget;
      const container = document.querySelector('.game-container');
      
      const ellipseRect = ellipse.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      
      const x1 = ellipseRect.left - containerRect.left + ellipseRect.width / 2;
      const y1 = ellipseRect.top - containerRect.top + ellipseRect.height / 2;
      const x2 = buttonRect.left - containerRect.left + buttonRect.width / 2;
      const y2 = buttonRect.top - containerRect.top + buttonRect.height / 2;
      
      setLineCoords({ x1, y1, x2, y2 });
      setShowLine(true);
      setShowFireworks(true);
      
      setTimeout(() => {
        setShowLine(false);
        setShowFireworks(false);
        setFeedback(null);
        setClickedButton(null);
        const next = idx + 1;
        if (next >= items.length) {
          setDone(true);
        } else {
          setIdx(next);
        }
      }, 1500);
    } else {
      setFeedback('wrong');
      
      // Play error sound
      ensureAudioReady().then((audioContext) => {
        if (!audioContext) return;
        playNote(200, 0.3, 'error');
      });
      
      // Wait 1 second, then show the line to one of the correct answers
      setTimeout(() => {
        setFeedback('showing-correct');
        
        // מצא תשובה נכונה אחת (לא משנה איזו מבין האפשרויות)
        const answerPool = current.options || PRONOUNS;
        const allCorrectAnswers = answerPool.filter(pron => current.conj[pron] === current.questionForm);
        const oneCorrectAnswer = allCorrectAnswers[0] || current.answerPronoun;
        
        const ellipse = document.querySelector('.verb-word-display');
        const correctButtonEl = document.querySelector(`[data-pronoun="${oneCorrectAnswer}"]`);
        const container = document.querySelector('.game-container');
        
        if (ellipse && correctButtonEl && container) {
          const ellipseRect = ellipse.getBoundingClientRect();
          const buttonRect = correctButtonEl.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          
          const x1 = ellipseRect.left - containerRect.left + ellipseRect.width / 2;
          const y1 = ellipseRect.top - containerRect.top + ellipseRect.height / 2;
          const x2 = buttonRect.left - containerRect.left + buttonRect.width / 2;
          const y2 = buttonRect.top - containerRect.top + buttonRect.height / 2;
          
          setLineCoords({ x1, y1, x2, y2 });
          setShowLine(true);
        }
        
        setTimeout(() => {
          setShowLine(false);
          setFeedback(null);
          setClickedButton(null);
          const next = idx + 1;
          if (next >= items.length) {
            setDone(true);
          } else {
            setIdx(next);
          }
        }, 1500);
      }, 1000);
    }
  }

  if (!items.length && !done) {
    return (
      <div className="App verb-game-who-did-it">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={handleBackToSubjects} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>טוען פעלים…</h1>
          </div>
        </header>
        <main className="game-container">
          <p>אנא בדוק את הקונסול (F12) לפרטי debug</p>
        </main>
      </div>
    );
  }

  if (done) {
    return (
      <div className="App verb-game-who-did-it">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={handleBackToSubjects} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>תוצאות המשחק</h1>
          </div>
        </header>
        <main className="game-container results-screen">
          <div className="words-summary" ref={resultsSummaryRef}>
            <h3>{gameConfig.title}</h3>
            <div className="final-score-display">
              ניקוד: {score}/{items.length}
            </div>
            <br/>
            <div className="words-list">
              {items.map((item, index) => {
                const userAnswer = userAnswers[index];
                const wasCorrect = userAnswer?.correct;
                const allCorrectAnswers = userAnswer?.allCorrectAnswers?.length
                  ? userAnswer.allCorrectAnswers
                  : (item.options || PRONOUNS).filter(pron => item.conj?.[pron] === item.questionForm);
                const displayPronouns = item.tense === 'עתיד' && allCorrectAnswers.length > 1
                  ? allCorrectAnswers
                  : [wasCorrect && userAnswer?.userChoice
                    ? userAnswer.userChoice
                    : item.answerPronoun];
                const shouldShowTense = gameConfig.tense === 'שניהם' || gameConfig.tense === 'שלושתם';
                const displayTense = item.tense === 'עתיד' ? 'הווה-עתיד' : item.tense;
                return (
                  <div key={index} className="word-item">
                    <div className="word-pair">
                      <span className="arabic-word-summary" lang="ar" dir="rtl">{item.questionForm}</span>
                      <span className="separator"> - </span>
                      <span className="hebrew-word result-answer" lang="ar" dir="rtl">
                        {displayPronouns.map((pronoun, pronounIndex) => (
                          <React.Fragment key={pronoun}>
                            {pronounIndex > 0 ? <span className="answer-separator">/</span> : null}
                            <span className="who-did-it-arabic-text" lang="ar" dir="rtl">{pronoun}</span>
                          </React.Fragment>
                        ))}
                        {shouldShowTense && displayTense ? (
                          <span className="answer-tense" lang="he" dir="rtl">({displayTense})</span>
                        ) : null}
                      </span>
                    </div>
                    <div className={`attempts-info ${!wasCorrect ? 'multiple-attempts' : ''}`}>
                      {wasCorrect ? (
                        <span>✓ תשובה נכונה</span>
                      ) : (
                        <span>
                          ✗ תשובתך: {userAnswer?.userChoice
                            ? <span className="who-did-it-arabic-text" lang="ar" dir="rtl">{userAnswer.userChoice}</span>
                            : 'לא נענה'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="game-end-buttons">
              <button onClick={() => window.location.reload()} className="game-button restart-button">
                שחק מחדש
              </button>
              <button onClick={() => setShowResultsPopup(true)} className="game-button send-button">
                שתף תוצאות
              </button>
            </div>
          </div>

          {showResultsPopup && (
            <div className="popup-overlay" onClick={() => setShowResultsPopup(false)}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                <button className="popup-close-button" onClick={() => setShowResultsPopup(false)}>
                  ×
                </button>
                <h3>שתף את התוצאות</h3>
                <p>בחר איך תרצה לשתף את התוצאות שלך:</p>
                <div className="popup-buttons">
                  <button onClick={shareViaEmail} className="popup-button email-button">
                    💾 הורד תמונה
                  </button>
                  <button onClick={shareViaWhatsApp} className="popup-button whatsapp-button">
                    📱 שתף בוואטסאפ
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="App verb-game-who-did-it">
      <header className="App-header">
        <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
          <button onClick={handleBackToSubjects} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
            חזרה למשחקי הפועל ↩
          </button>
          <h1 className="game-title-with-back" style={{ margin: 0 }}>{gameConfig.title}</h1>
        </div>
        <p className="game-instructions-animate">לחץ על כינוי הגוף שמתאים לפועל</p>
      </header>

      <main className={`game-container ${showFireworks ? 'celebrating' : ''}`} style={{ position: 'relative' }}>
        {showLine && (
          <svg style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 1000,
            overflow: 'visible'
          }}>
            <line
              x1={lineCoords.x1}
              y1={lineCoords.y1}
              x2={lineCoords.x2}
              y2={lineCoords.y2}
              stroke={feedback === 'correct' ? '#4caf50' : '#ffeb3b'}
              strokeWidth="4"
              strokeDasharray="10,5"
              style={{
                animation: 'drawLine 0.5s ease-out forwards'
              }}
            />
          </svg>
        )}
        
        {showFireworks && (
          <div className="fireworks-container">
            <div className="firework firework-1"></div>
            <div className="firework firework-2"></div>
            <div className="firework firework-3"></div>
            <div className="firework firework-4"></div>
            <div className="firework firework-5"></div>
            <div className="firework firework-6"></div>
            <div className="firework firework-7"></div>
            <div className="firework firework-8"></div>
            <div className="firework firework-9"></div>
            <div className="firework firework-10"></div>
            <div className="firework firework-11"></div>
            <div className="firework firework-12"></div>
          </div>
        )}
        
        <div className="verb-display-container">
          <div className="verb-word-display">
            <div className="instruction-text">
              <p className="verb-arabic-word" lang="ar" dir="rtl" style={{ wordSpacing: 0 }}>{current.questionForm}</p>
            </div>
          </div>
          <div className="verb-progress">{progress}</div>
        </div>

        <div className="answers-grid">
          {(current?.options || (isMobile ? PRONOUNS_MOBILE : PRONOUNS)).map(opt => (
            <button
              key={opt}
              data-pronoun={opt}
              className={`answer-chip 
                ${clickedButton === opt && feedback === 'wrong' ? 'wrong' : ''} 
                ${clickedButton === opt && feedback === 'correct' ? 'correct' : ''}`}
              onClick={(e) => handleChoose(opt, e)}
              disabled={!!feedback}
              lang="ar"
              dir="rtl"
            >
              {opt}
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
